<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Parallax from '$lib/Components/Parallax/Parallax.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import AverageSaved from '$lib/PageSections/Charts/AverageSaved/AverageSaved.svelte';
	import CustomizeYourInsert from '$lib/PageSections/CustomizeYourInsert/CustomizeYourInsert.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import WhatDoTheyCost from '$lib/PageSections/WhatDoTheyCost/WhatDoTheyCost.svelte';
	// images
	import image1 from './images/installing.png';
	import image2 from './images/standard-grade-1.jpg';
	import image4 from './images/standard-grade-bottom.png';
</script>

<Section backgroundColor={'white'}>
	<h2>What are Interior Storm Windows?</h2>
	<Image
		src={image1}
		alt="man installing interior window insert."
		class="w-72"
	/>
	<p>
		The Indow Standard Grade interior storm windows are an innovative,
		cost-effective way to block drafts and noise to make your home comfortable
		and energy efficient. We designed these inserts to help your existing
		windows perform like new double-panes all while blending into the frames so
		they’re nearly invisible. They install in seconds without disruption or
		mess.
	</p>
	<Image
		src={image2}
		alt="interior storm windows or interior window inserts."
	/>
	<p>
		Unlike conventional interior storm windows, our indoor storm windows press
		inside your existing window frames without a damaging mounting bracket. Save
		money on energy bills while keeping your original windows to preserve your
		home’s character and value.
	</p>
	<Bubble title="Standard grade custom indoor storm window inserts:">
		<ul>
			<li>
				<p class="text-left">Nominal ⅛” thick acrylic glazing.</p>
			</li>
			<li>
				<p class="text-left">
					Edged in our patented silicone compression tubing.
				</p>
			</li>
			<li>
				<p class="text-left">
					Made in three different colors - warm white, brown and black.
				</p>
			</li>
			<li>
				<p class="text-left">
					Available in custom shapes like arches and rounds.
				</p>
			</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor={'white'}>
	<h2>How Custom Storm Window Inserts Work</h2>
	<p>
		Because houses settle, most windows aren’t square. At Indow, we laser
		measure every window opening and then custom make each insert, ensuring a
		precise, snug fit for an airtight seal that blocks drafts. That compression
		is also what holds the insert in place.
	</p>
	<p>
		The window inserts blend in and can’t be seen from the exterior of the
		house, so don’t violate historic district guidelines. People with newer
		double-pane windows use Indow inserts to enhance their performance.
	</p>
	<p>
		Standard Grade inserts are suitable for any room. Some homeowners start by
		installing inside storm windows in their draftiest space and over time buy
		inserts for the rest of their home.
	</p>
	<Youtube src="https://www.youtube.com/embed/nJeWCeGR6xE" />
</Section>

<Section backgroundColor={'white'}>
	<h2>Testimonials on Custom Storm Window Inserts</h2>
	<p>
		Instead of replacing the historic windows in his home, Kester installed
		Indow Standard Grade interior storm window inserts. He immediately improved
		the performance of his windows.
	</p>
	<p class="!text-indow-blue text-lg italic">
		“What attracted me to the Indow window insert product was that you don’t
		mess with your house at all. You just press the inserts into your window
		frames. There’s no mounting bracket. They’re fitted perfectly to each window
		so it’s just the pressure gasket that allows you to insert it into the
		window. They’re light and easy to install and remove again and again.”
	</p>
	<p>
		For partners <a href="/resources/case-studies/stop-window-condensation"
			>Greg and Eddie</a
		>, using Standard Grade eliminated condensation in their mid-century modern
		Texas home. But Indow inserts are versatile. For a slightly higher price,
		our specialty grades offer an impressive array of solutions for the myriad
		challenges posed by windows whether it’s the need to block sunlight or UV
		rays or create privacy or quiet.
	</p>
	<p class="!text-indow-blue text-lg italic">
		“I couldn’t be more pleased,” said Greg. “The Indow inserts have a very low
		profile.”
	</p>
	<Youtube src="https://www.youtube.com/embed/1MZrZOte5Is" />
	<Bubble title="How Standard Grade Storm Window Inserts Perform:">
		<ul>
			<li>
				<p class="text-left">
					Saves <a href="/static/pdfs/PNNL-22855.pdf">up to 20% on energy</a>,
					whether winter heating bills or summer air conditioning.
				</p>
			</li>
			<li>
				<p class="text-left">
					50 percent noise reduction (10-12 dBA) when placed over single-pane
					windows.*
				</p>
			</li>
			<li>
				<p class="text-left">
					<a href="/custom-storm-windows/acoustic-grade">Acoustic Grade</a> is the
					best option for quieting outside noise and will achieve a 70 percent noise
					reduction (18.9 dBA) when placed over single-pane windows.
				</p>
			</li>
		</ul>
	</Bubble>
	<p class="!text-indow-blue text-lg italic">
		<a href="/solutions/comfort-performance">
			Explore complete performance data here.
		</a>
	</p>
</Section>
<WhatDoTheyCost version="2" />
<AverageSaved>
	<p class="place-self-center text-sm italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</AverageSaved>
<GetAFreeEstimate />
<Reviews />
<Parallax image={image4} />
<CustomizeYourInsert />
